import { Box, CircularProgress, Drawer, IconButton } from "@mui/material"
import CompareArrowsIcon from "@mui/icons-material/CompareArrows"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { useCallback, useEffect, useState } from "react"
import {
  type MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from "material-react-table"
import { useGetModelQuery } from "@store/services/modelsService"
import { skipToken } from "@reduxjs/toolkit/dist/query/react"
import Grid from "@mui/material/Grid"
import { ChartsContainer } from "@features/results/Result/ChartsContainer/ChartsContainer"
import { RunDto } from "@custom-types/modelRun"
import {
  useGetGraphDataQuery,
  useGetGraphQuery,
  useGetUserGraphDataQuery,
  useGetUserGraphQuery,
} from "@store/services/graphsService"
import { useGetRunsByKeysQuery } from "@store/services/runService"
import {
  GraphDataViewModel,
  GraphEditMode,
  GraphNodeSelection,
  GroupNodeLabel,
} from "@features/graph/graphViewModel"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import { HistoryTwoTone } from "@mui/icons-material"
import { TornadoChart } from "./TornadoChart"
import { DifferenceChart } from "./Difference"
import { GraphModelDiffTable } from "./GraphDiffTable"
import { getRunName } from "utils/run"
import { GraphNodeType } from "@custom-types/graphs"
import { SplitButton } from "@features/results/Result/SplitButton"
import { GraphContainer } from "@features/graph/GraphContainer"
import NodeSelect from "@components/NodeSelect"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import { ComparisonMetadataCard } from "@features/results/Comparison/ComparisonMetadataCard"
import {
  delDashboardParamUrl,
  graphUrlAddNode,
  graphUrlClearNodes,
  graphUrlGetFirstNodeLabel,
  setDashboardParamUrl,
} from "@features/graph/utils/graphUrl"
import ShowDashboardButton from "@features/results/ShowDashboardButton"
import { DashboardMode, DrawerDashboard } from "@features/results/Result/DrawerDashboard"

enum CompareType {
  Tornado,
  Graph_Diff,
}

const columns: MRT_ColumnDef<RunDto>[] = [
  {
    accessorFn: (run) => getRunName(run),
    header: "Name",
    size: 150,
  },
  {
    accessorKey: "key",
    header: "Run ID",
    size: 150,
  },
  // {
  //   accessorKey: "creation_date",
  //   header: "Date",
  //   size: 150,
  // },
  {
    accessorKey: "version",
    header: "Version",
    size: 50,
  },
  // {
  //   accessorKey: "seed",
  //   header: "Seed",
  //   size: 40,
  // },
  // {
  //   accessorKey: "num_samples",
  //   header: "Samples",
  //   size: 50,
  // },
]

export default function Comparison() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const [graphToPresent, setGraphToPresent] = useState<CompareType | null>(null)
  const [runToCompare, setRunToCompare] = useState<RunDto | null>(null)
  const [graphSelectedNode, setGraphSelectedNode] = useState<GraphNodeData | null>(null)
  const [graphSelectedGroup, setGraphSelectedGroup] = useState<GroupNodeData | null>(null)
  const [nodeGroupSelectionValue, setNodeGroupSelectionValue] = useState<GroupNodeLabel | null>(
    null,
  )
  const [isDrawerNodeVisible, setIsDrawerNodeVisible] = useState(false)
  const [isDrawerCompareVisible, setIsDrawerCompareVisible] = useState(false)
  const [isDrawerDashboardVisible, setIsDrawerDashboardVisible] = useState(false)

  const runKeys = searchParams.getAll("runKeys")
  const { isFetching: isFetchingRuns, data: runsResponse } = useGetRunsByKeysQuery(runKeys)
  const runs: RunDto[] = Object.values(runsResponse ?? {})
  const isCategoryViewEnabled = searchParams.get("isCategoriesViewEnabled") === true.toString()

  const table = useMaterialReactTable({
    columns,
    data: runs,
    enableColumnFilterModes: false,
    enableDensityToggle: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableGlobalFilter: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    layoutMode: "grid",
    getRowId: (row) => row.key,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 80,
        header: "Sensitivity",
        grow: true,
      },
    },
    renderRowActions: ({ row }) => {
      const isSelectedRow = row.id === Object.keys(rowSelection)[0]
      if (isSelectedRow) return <></>

      return (
        <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
          <Tooltip
            title={
              graphSelectedNode
                ? "Show tornado sensitivity run-to-run for all timesteps"
                : "Please, select a node"
            }
          >
            <Box>
              <IconButton
                disabled={!graphSelectedNode}
                onClick={() => {
                  setRunToCompare(row.original)
                  setGraphToPresent(CompareType.Tornado)
                  setIsDrawerCompareVisible(true)
                }}
              >
                <CompareArrowsIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Show base graph shape comparison run vs. run">
            <IconButton
              onClick={() => {
                setRunToCompare(row.original)
                setGraphToPresent(CompareType.Graph_Diff)
                setIsDrawerCompareVisible(true)
              }}
            >
              <HistoryTwoTone />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    enableRowSelection: true,
    enablePagination: false,
    enableBottomToolbar: false,
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "#d6002a", //you might want to change the cursor too when adding an onClick
        color: "white",
      },
    },
    enableMultiRowSelection: false,
    initialState: {
      density: "compact",
      showColumnFilters: false,
      showGlobalFilter: true,
      columnVisibility: { key: false },
    },
    renderTopToolbar: () => {
      return (
        <Box
          sx={(_theme) => ({
            display: "flex",
            p: "2px",
            justifyContent: "space-between",
          })}
        ></Box>
      )
    },
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
  })

  const selectedRunKey = Object.keys(rowSelection)[0]
  const selectedRun = selectedRunKey ? runs.find(({ key }) => key === selectedRunKey) : undefined

  const { data: runModel, isFetching: isFetchingModel } = useGetModelQuery(
    selectedRun?.model_key || skipToken,
  )

  const skipBaseGraph = selectedRun?.user_graph_key || !selectedRun?.base_graph_key
  const skipUserGraph = !selectedRun?.user_graph_key

  const { data: baseGraph, isFetching: isFetchingBaseGraph } = useGetGraphQuery(
    skipBaseGraph ? skipToken : selectedRun?.base_graph_key,
  )
  const { data: userGraph, isFetching: isFetchingUserGraph } = useGetUserGraphQuery(
    skipUserGraph ? skipToken : selectedRun?.user_graph_key,
  )

  const graph = userGraph ?? baseGraph
  const skipUserGraphData = !userGraph?.key
  const skipBaseGraphData = !baseGraph?.key
  const { data: uggraphData, isFetching: isFetchingUserGraphData } = useGetUserGraphDataQuery(
    skipUserGraphData ? skipToken : userGraph?.key,
  )
  const { data: bgraphData, isFetching: isFetchingBaseGraphData } = useGetGraphDataQuery(
    skipBaseGraphData ? skipToken : baseGraph?.key,
  )
  const graphData = uggraphData ?? bgraphData

  const isFetching =
    isFetchingRuns ||
    isFetchingModel ||
    isFetchingBaseGraph ||
    isFetchingBaseGraphData ||
    isFetchingUserGraph ||
    isFetchingUserGraphData

  const loadSelectedNodeFromQuery = useCallback(() => {
    if (!GraphDataViewModel.data) return

    const nodeLabel = searchParams.getAll("nodeLabel")[0]
    if (!nodeLabel) return

    const selectedNodeEntry = Object.entries(GraphDataViewModel.data.labelMap).find(
      ([_, value]) => value === nodeLabel,
    )
    if (!selectedNodeEntry) return

    const [nodeId] = selectedNodeEntry
    const selectedNode = GraphDataViewModel.data.nodeMap[nodeId]
    if (!selectedNode) return

    setGraphSelectedNode(selectedNode)
  }, [searchParams])

  const loadDrawerFromUrl = useCallback(() => {
    if (!GraphDataViewModel.data) return
    const node = graphUrlGetFirstNodeLabel(GraphDataViewModel.data)
    if (!node) return
    setGraphSelectedNode(node)
    setIsDrawerNodeVisible(true)
  }, [])

  useEffect(() => {
    if (isFetchingRuns) return
    // load drawer from url
    if (!GraphDataViewModel.data) {
      GraphDataViewModel.onLoad(() => {
        loadSelectedNodeFromQuery()
        loadDrawerFromUrl()
      })
    } else {
      loadSelectedNodeFromQuery()
      loadDrawerFromUrl()
    }
  }, [isFetchingRuns, loadDrawerFromUrl, loadSelectedNodeFromQuery])

  useEffect(() => {
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    if (runs.length > 0) setRowSelection({ [runs[0].key]: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [runsResponse])

  useEffect(() => {
    if (!graph) return

    setSearchParams((params) => {
      params.set("graphKey", graph.key)

      return params
    })
  }, [graph, setSearchParams])

  const openCompareNodeDrawer = useCallback(
    (node: GraphNodeData | null) => {
      if (!node) return
      setIsDrawerNodeVisible(true)

      navigate({
        pathname: location.pathname,
        search: location.search,
        hash: encodeURIComponent(node.display.label),
      })
    },
    [location.pathname, location.search, navigate],
  )

  const selectionChangeCallback = useCallback(
    (selection: GraphNodeSelection) => {
      // select only one group
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      if (selection.nodes.length === 1 && selection.nodes[0].type === GraphNodeType.GROUP) {
        const group = selection.nodes[0]!
        const newSelectedGroup = GraphDataViewModel.data!.groupMap[group.id] ?? null
        setGraphSelectedGroup(newSelectedGroup)
        setGraphSelectedNode(null)

        graphUrlClearNodes()
        return
      }

      if (selection.nodes.length === 1) {
        const node = selection.nodes.find(
          (node) =>
            node.data?.display?.label &&
            node.type !== GraphNodeType.GROUP &&
            node.type !== GraphNodeType.TABLE,
        )
        if (node) {
          const graphData = GraphDataViewModel.data?.nodeMap[node.id] ?? null
          setGraphSelectedNode(graphData)
          setNodeGroupSelectionValue(null)
          setGraphSelectedGroup(null)
          graphUrlAddNode(node.data.display.label, true)

          // if selection is made in category view, open compare drawer automatically without need of clicking button
          if (isCategoryViewEnabled) {
            openCompareNodeDrawer(graphData)
          }
        }
      } else {
        graphUrlClearNodes()
        setGraphSelectedNode(null)
        setNodeGroupSelectionValue(null)
        setGraphSelectedGroup(null)
      }
    },
    [isCategoryViewEnabled, openCompareNodeDrawer],
  )

  const handleChangeNodeSelect = (newValue: GroupNodeLabel | null) => {
    setNodeGroupSelectionValue(newValue)
    if (!newValue) {
      return
    }

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    setGraphSelectedNode(GraphDataViewModel.data!.nodeMap[newValue.key])
  }
  const handleShowDashboardClick = () => {
    setIsDrawerDashboardVisible(true)
    setDashboardParamUrl("")
  }

  const compareInstruction = "Select a node in the graph, then click 'Compare Node Results'"

  return (
    <Box sx={{ mt: 0 }}>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid
          container
          sx={{ height: "calc(100vh - 64px)", pl: 0 }}
          wrap="nowrap"
          gap={1}
          direction={"column"}
        >
          <Grid item xs gap={1}>
            {/* RTK limitation, graph container itself would also immidietely start to fetch the same graph data
            and it would cause infite loop, so let's wait here for the data, graph container will use fetched (cached) data */}
            {!isFetchingUserGraph && !isFetchingBaseGraph && (
              <GraphContainer
                graph={graph}
                graphData={graphData}
                showAddToSelection={false}
                isUserGraph={!!runModel?.user_graph_key}
                showModified={false}
                showExportGraph={false}
                showResetGraph={false}
                showUserGraphIndicator={true}
                selectionChangeCallback={selectionChangeCallback}
                onFetchingError={() => {}}
                editMode={GraphEditMode.OFF}
                graphFetchingErrorMessage="Preview can't be loaded"
                isMultiselectVisible={true}
                isMultipleRuns={true}
              />
            )}
          </Grid>
          <Grid container>
            <ComparisonMetadataCard
              runName={`Comparison of selected ${runModel?.family_key?.replaceAll(
                "#",
                " → ",
              )} results`}
              selectedRun={selectedRun}
              runModel={runModel}
              table={table}
            />
          </Grid>
          <Grid item container>
            {graphSelectedGroup && (
              <Grid item xs={3}>
                <NodeSelect
                  graph={graphData}
                  value={nodeGroupSelectionValue}
                  onChange={handleChangeNodeSelect}
                  limitToGroupNodes={[graphSelectedGroup.id]}
                  sx={{ width: 400 }}
                />
              </Grid>
            )}
            <Grid item>
              <SplitButton
                title="Compare Node Results"
                instruction={compareInstruction}
                properties={{
                  disabled: !graphSelectedNode,
                  onClick: () => openCompareNodeDrawer(graphSelectedNode),
                }}
              />
            </Grid>
            {graph?.dashboard_s3_location && (
              <Grid item>
                <ShowDashboardButton onClick={handleShowDashboardClick} />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      <Drawer
        open={isDrawerNodeVisible}
        anchor="right"
        hideBackdrop={true}
        PaperProps={{
          sx: { width: "95vw" },
        }}
        ModalProps={{
          slots: { backdrop: "div" },
          slotProps: {
            root: {
              style: {
                position: "absolute",
                top: "unset",
                bottom: "unset",
                left: "unset",
                right: "unset",
              },
            },
          },
        }}
      >
        <Box sx={{ m: 2 }}>
          <Button
            onClick={() => {
              setIsDrawerNodeVisible(false)
              window.location.hash = ""
            }}
            variant="contained"
            sx={{
              width: "100px",
              position: "fixed",
              top: "12px",
              right: "24px",
            }}
          >
            Close
          </Button>
          {graphSelectedNode && (
            <ChartsContainer runs={runs} selectedNode={graphSelectedNode.display.label} />
          )}
        </Box>
      </Drawer>
      <Drawer
        open={isDrawerCompareVisible}
        anchor="left"
        hideBackdrop={true}
        PaperProps={{
          sx: { width: "95vw" },
        }}
        ModalProps={{
          slots: { backdrop: "div" },
          slotProps: {
            root: {
              style: {
                position: "absolute",
                top: "unset",
                bottom: "unset",
                left: "unset",
                right: "unset",
              },
            },
          },
        }}
      >
        <Box sx={{ m: 2 }}>
          <Button
            onClick={() => setIsDrawerCompareVisible(false)}
            variant="contained"
            sx={{
              zIndex: 100,
              width: "100px",
              position: "fixed",
              top: "12px",
              right: "7vw",
            }}
          >
            Close
          </Button>
        </Box>
        {graphToPresent === CompareType.Tornado && [
          // eslint-disable-next-line react/jsx-key -- fix it when you will have a chance to check this component
          <TornadoChart
            run1={selectedRun}
            run2={runToCompare ?? undefined}
            node={graphSelectedNode}
          />,
          // eslint-disable-next-line react/jsx-key -- fix it when you will have a chance to check this component
          <DifferenceChart
            run1={selectedRun}
            run2={runToCompare ?? undefined}
            node={graphSelectedNode}
          />,
        ]}
        {graphToPresent === CompareType.Graph_Diff && selectedRun && runToCompare && (
          <GraphModelDiffTable data1={selectedRun} data2={runToCompare ?? undefined} />
        )}
      </Drawer>
      {selectedRun && (
        <Drawer
          open={isDrawerDashboardVisible}
          anchor="right"
          PaperProps={{
            sx: { width: "100vw" },
          }}
        >
          <DrawerDashboard
            runKeys={runKeys}
            selectedRunKey={selectedRun.key}
            mode={DashboardMode.CompareResults}
            onClose={() => {
              setIsDrawerDashboardVisible(false)
              delDashboardParamUrl()
            }}
          />
        </Drawer>
      )}
    </Box>
  )
}
